import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useTripTypeServiceV1BatchCreateTripTypeMutation } from "_services/modules/trip_type";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface TripTypeItem {
  tripTypeName: string;
  description: string;
  status: string;
  isFreezer: boolean;
}

export interface AddTripTypeFormValues {
  tripTypeName: string;
  description: string;
  status: string;
  isFreezer: boolean;
  items: TripTypeItem[];
}

const schema = yup.object({
  tripTypeName: yup.string().required("Trip Type Name is required").max(100, "Max 100 characters"),
  description: yup.string().max(500, "Max 500 characters").default(""),
  status: yup.string().required("Status is required"),
  isFreezer: yup.boolean().default(false),
  items: yup.array().of(
    yup.object({
      tripTypeName: yup.string().required(),
      description: yup.string().default(""),
      status: yup.string().required(),
      isFreezer: yup.boolean().default(false),
    })
  ).required(),
}).required();

const useAddTripTypeForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("tripType");

  const [batchCreateTripType] = useTripTypeServiceV1BatchCreateTripTypeMutation();

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<AddTripTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      tripTypeName: "",
      description: "",
      status: "active",
      isFreezer: false,
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const handleAddToList = async () => {
    const isValid = await trigger(["tripTypeName", "status"]);
    if (!isValid) return;

    const { tripTypeName, description, status, isFreezer } = getValues();

    const isDuplicate = fields.some(
      (item, index) =>
        item.tripTypeName.toLowerCase() === tripTypeName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("tripTypeName", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    if (editingIndex !== null) {
      update(editingIndex, { tripTypeName, description, status, isFreezer });
      setEditingIndex(null);
    } else {
      append({ tripTypeName, description, status, isFreezer });
    }

    setValue("tripTypeName", "");
    setValue("description", "");
    setValue("status", "active");
    setValue("isFreezer", false);
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
    setValue("tripTypeName", fields[index].tripTypeName);
    setValue("description", fields[index].description);
    setValue("status", fields[index].status);
    setValue("isFreezer", fields[index].isFreezer);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      setValue("tripTypeName", "");
      setValue("description", "");
      setValue("status", "active");
      setValue("isFreezer", false);
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    setValue("tripTypeName", "");
    setValue("description", "");
    setValue("status", "active");
    setValue("isFreezer", false);
  };

  const handleSave = async () => {
    try {
      const payload = {
        trip_types: fields.map((f) => ({
          trip_type_name: f.tripTypeName,
          description: f.description,
          status: f.status,
          is_freezer: f.isFreezer,
        })),
      };
      await batchCreateTripType({ tripTypeV1BatchCreateTripTypeReq: payload }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    t,
  };
};

export default useAddTripTypeForm;
